import { typescriptLanguage } from '@codemirror/lang-javascript';
import { pythonLanguage } from '@codemirror/lang-python';
import { LanguageSupport } from '@codemirror/language';
import {
  Button,
  CircularProgress,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';

import dedent from 'dedent';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useNavigate } from 'react-router-dom';

import { Example } from '@/Pages/Example';
import {
  Code,
  CodeCard,
  CodeDataEditor,
  CodeLanguageSelect,
} from '@/components/Code/Code';
import { getJsonFromSchema } from '@/components/ExampleCrudPane/utils';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { SplitViewPane } from '@/components/SplitViewPane';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useLanguagePreference } from '@/hooks/useLanguagePreferenceContext/useLanguagePreference';
import {
  useDatasetVersionsInfinite,
  useDesyncDatasetForFewShotMutation,
  useExample,
  useExamples,
  useSearchDatasetMutation,
} from '@/hooks/useSwr';
import ArrowRightIcon from '@/icons/ArrowRightIcon.svg?react';
import CodeIconWithOutline from '@/icons/CodeIconWithOutline.svg?react';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import {
  DatasetSchema,
  FewShotSyncStatusSchema,
  SearchedFewShotExample,
} from '@/types/schema';
import { appExamplePath } from '@/utils/constants';
import { jsonSchemaExtensions } from '@/utils/use-codemirror-tooltip-follow-mouse';
import { localTime } from '@/utils/utc-time';

const ExamplePane = ({
  examplePeekId,
  onMutate,
  setExamplePeekId,
  asOfRaw,
  onNext,
  onPrevious,
}: {
  examplePeekId: string | null;
  onMutate: () => void;
  setExamplePeekId: (id: string | null) => void;
  asOfRaw: string | null;
  onNext?: () => void;
  onPrevious?: () => void;
}) => {
  const navigate = useNavigate();
  const peekFetch = useExample(examplePeekId);
  return (
    <SplitViewPane
      open={!!examplePeekId}
      sidePaneId="examplesTable"
      onClose={() => {
        setExamplePeekId(null);
      }}
      onExpand={() => {
        if (examplePeekId) {
          navigate(
            `${appExamplePath}/${examplePeekId}${
              asOfRaw ? `?as_of=${encodeURIComponent(asOfRaw)}` : ''
            }`
          );
        }
      }}
      title={
        peekFetch.isLoading && (
          <>
            <div className="mx-2 h-[32px] self-stretch border-r border-r-secondary" />
            <CircularProgress
              size="sm"
              color="neutral"
              sx={{ opacity: 0.5, transform: 'scale(0.83)' }}
            />
          </>
        )
      }
      onNext={onNext}
      onPrevious={onPrevious}
      className="relative"
    >
      <div className="absolute inset-0 flex flex-grow flex-col gap-4 overflow-auto p-4">
        <Example exampleId={examplePeekId} onMutate={onMutate} />
      </div>
    </SplitViewPane>
  );
};

const useRemainingHeight = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [yOffset, setYOffset] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const { y } = containerRef.current.getBoundingClientRect();
      setYOffset(y);
    }
  }, []);

  return { containerRef, yOffset };
};

const ResultView = ({
  result,
  datasetVersion,
  onMutate,
}: {
  result: SearchedFewShotExample[];
  datasetVersion?: string;
  onMutate: () => void;
}) => {
  const { containerRef, yOffset } = useRemainingHeight();
  const [examplePeekId, setExamplePeekId] = useState<string | null>(null);
  const examplePeekIndex = result.findIndex(
    (example) => example.id === examplePeekId
  );
  const handleNext =
    examplePeekIndex === result.length - 1
      ? undefined
      : () => {
          const nextId = result[examplePeekIndex + 1]?.id;
          setExamplePeekId(nextId);
        };

  const handlePrevious =
    examplePeekIndex === 0
      ? undefined
      : () => {
          const previousId = result[examplePeekIndex - 1]?.id;
          setExamplePeekId(previousId);
        };

  return (
    <div
      ref={containerRef}
      style={{ height: `calc(100vh - ${yOffset}px)` }}
      className="-mb-6"
    >
      {result.length > 0 ? (
        <div className="flex flex-col gap-4 pl-4 pt-4">
          <div className="">Search results</div>

          <div className="flex flex-col gap-4 rounded-lg border border-secondary">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[7%] pl-5">SCORE</TableHead>
                  <TableHead className="w-[13%] pl-5">EXAMPLE</TableHead>
                  <TableHead className="w-[40%]">INPUT</TableHead>
                  <TableHead className="w-[40%]">OUTPUT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.map((example) => (
                  <TableRow key={example.id}>
                    <TableCell className="w-[7%] pl-5">
                      <button
                        type="button"
                        onClick={() => setExamplePeekId(example.id)}
                        className="flex items-center justify-center gap-1 rounded-md bg-secondary px-2 py-1"
                      >
                        {example.debug_info?.score?.toFixed(2)}{' '}
                      </button>
                    </TableCell>
                    <TableCell className="w-[13%] pl-5">
                      <button
                        type="button"
                        onClick={() => setExamplePeekId(example.id)}
                        className="flex w-[72px] items-center justify-center gap-1 rounded-md bg-secondary p-1"
                      >
                        #{example.id.slice(0, 4)}{' '}
                        <ArrowRightIcon className="h-4 w-4" />
                      </button>
                    </TableCell>
                    <TableCell className="w-[40%]">
                      <div className="line-clamp-2">
                        {JSON.stringify(example.inputs, null, 2)}
                      </div>
                    </TableCell>
                    <TableCell className="w-[40%]">
                      <div className="line-clamp-2">
                        {JSON.stringify(example.outputs, null, 2)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ExamplePane
            onMutate={onMutate}
            examplePeekId={examplePeekId}
            setExamplePeekId={setExamplePeekId}
            asOfRaw={datasetVersion ?? null}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        </div>
      ) : (
        <div
          className="flex h-full w-full justify-center bg-secondary pt-16"
          ref={containerRef}
        >
          <div className="text-sm">Search results will appear here</div>
        </div>
      )}
    </div>
  );
};

function DesyncButton(props: { datasetId: string; tag: string }) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const desyncMutation = useDesyncDatasetForFewShotMutation(props.datasetId);

  const handleDelete = async () => {
    await desyncMutation.trigger({
      json: {
        tag: props.tag,
      },
    });

    setIsConfirmOpen(false);
  };

  return (
    <Popover open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
      <PopoverTrigger>
        <TrashIcon className="h-5 w-5 cursor-pointer" />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4">
        <h3 className="mb-2 text-sm font-semibold">Confirm delete index</h3>
        <p className="mb-4 text-sm">
          <div>Are you sure you want to delete this index</div>
          <div>and de-sync the dataset?</div>
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-secondary px-3 py-1 text-xs hover:bg-secondary"
            onClick={() => setIsConfirmOpen(false)}
          >
            Cancel
          </button>
          {desyncMutation.isMutating ? (
            <CircularProgress size="sm" />
          ) : (
            <button
              type="button"
              className="rounded-md bg-red-500 px-3 py-1 text-xs text-white hover:bg-red-600"
              onClick={handleDelete}
            >
              De-sync
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
const SearchView = ({
  onSearch,
  searchError,
  inputSchema,
  datasetId,
  syncStatus,
}: {
  onSearch: (value: Record<string, any>) => void;
  searchError: Error | undefined;
  inputSchema: DatasetSchema['inputs_schema_definition'];
  datasetId: string;
  syncStatus?: FewShotSyncStatusSchema;
}) => {
  const [value, setValue] = useState<Record<string, any>>(
    getJsonFromSchema(inputSchema)
  );
  const [codeModalOpen, setCodeModalOpen] = useState(false);

  const [userLang, setUserLang] = useLanguagePreference();

  const { data: versionPages } = useDatasetVersionsInfinite(
    1,
    datasetId ?? null,
    // Don't fetch versions if we don't have a tag on some of the initial renders
    syncStatus?.tag
      ? {
          search: syncStatus?.tag,
        }
      : null
  );

  const syncedVersionTimestamp = versionPages?.[0]?.[0]?.as_of;

  const lastUpdated = useMemo(() => {
    if (!syncStatus?.last_updated_version) {
      return 'Indexing...';
    }
    const syncUpdated = new Date(syncStatus.last_updated_version);
    if (syncedVersionTimestamp === syncStatus.last_updated_version) {
      return `Index up to date with ${syncStatus.tag}`;
    }
    return `Index synced through version: ${localTime(syncUpdated).format(
      'MM/DD/YYYY, HH:mm:ss'
    )}`;
  }, [
    syncStatus?.last_updated_version,
    syncStatus?.tag,
    syncedVersionTimestamp,
  ]);

  return (
    <div className="flex flex-col gap-4 pr-4">
      <div className="flex justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 font-semibold">
            Few-shot index
            <DesyncButton datasetId={datasetId} tag={syncStatus?.tag ?? ''} />
          </div>
          <div className="text-grey-500 text-xs dark:text-gray-400">
            {lastUpdated}
          </div>
        </div>
        <div>
          <Button
            variant="outlined"
            color="neutral"
            size="md"
            onClick={() => {
              setCodeModalOpen(true);
            }}
            startDecorator={<CodeIconWithOutline className="h-6 w-6" />}
          >
            Get Code Snippet
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-4 rounded-lg border border-secondary p-4">
        <div className="text-sm font-medium">INPUT</div>
        <div className="flex flex-col gap-2 rounded-md bg-secondary">
          <CodeDataEditor
            value={value}
            language={userLang}
            onChange={(value) => {
              setValue(value);
            }}
            extensions={
              userLang === 'json' ? jsonSchemaExtensions(inputSchema) : []
            }
          />
          <div className="max-w-[100px] flex-none">
            <CodeLanguageSelect value={userLang} onChange={setUserLang} />
          </div>
        </div>
        {searchError && <ExpandableErrorAlert error={searchError} />}
        <div className="flex justify-end">
          <Button onClick={() => onSearch(value)}>Search</Button>
        </div>
      </div>
      <CodeSnippetModal
        open={codeModalOpen}
        onClose={() => setCodeModalOpen(false)}
        datasetId={datasetId}
        search_value={value}
        input_schema={inputSchema}
      />
    </div>
  );
};
export const FewShotSearch = ({
  datasetId,
  inputSchema,
  syncStatus,
  onMutate,
}: {
  datasetId: string;
  inputSchema: DatasetSchema['inputs_schema_definition'];
  syncStatus?: FewShotSyncStatusSchema;
  onMutate: () => void;
}) => {
  const { trigger, data, error, isMutating } =
    useSearchDatasetMutation(datasetId);

  const handleSearch = (value: Record<string, any>) => {
    trigger({ json: { inputs: value, debug: true } });
  };
  return (
    <PanelGroup direction="horizontal" className="-mt-4">
      <Panel minSize={30} className="flex flex-col pt-4" id="peeked-trace-view">
        <SearchView
          onSearch={handleSearch}
          searchError={error}
          inputSchema={inputSchema}
          datasetId={datasetId}
          syncStatus={syncStatus}
        />
      </Panel>
      <PanelResizeHandle className="group relative flex items-stretch text-secondary data-[resize-handle-active]:text-brand-green-400">
        <div className="w-[1px] bg-tertiary" />
        <div className="absolute inset-y-0 w-[2px] bg-current opacity-0 transition-all group-hover:opacity-100" />
      </PanelResizeHandle>
      <Panel minSize={30} className="flex flex-col" id="compare-trace-view">
        {isMutating ? (
          <div>
            <LinearProgress />
          </div>
        ) : (
          <ResultView
            result={data?.examples || []}
            datasetVersion={syncStatus?.last_updated_version}
            onMutate={onMutate}
          />
        )}
      </Panel>
    </PanelGroup>
  );
};

const CodeSnippetModal = (props: {
  open: boolean;
  onClose: () => void;
  datasetId: string;
  search_value?: Record<string, unknown>;
  input_schema?: Record<string, unknown>;
}) => {
  const { input_schema, search_value } = props;

  const valueEdited = useMemo(() => {
    return (
      JSON.stringify(search_value) !==
      JSON.stringify(getJsonFromSchema(input_schema))
    );
  }, [search_value, input_schema]);

  const example = useExamples(
    !valueEdited
      ? {
          dataset: props.datasetId,
          limit: 1,
        }
      : null
  );
  const inputValue =
    search_value && valueEdited
      ? search_value
      : example.data?.[0]?.inputs &&
        JSON.stringify(example.data?.[0]?.inputs).length < 1000
      ? example.data?.[0]?.inputs
      : getJsonFromSchema(props.input_schema);
  const searchText = JSON.stringify(inputValue, null, 2);
  const { isDarkMode } = useColorScheme();

  const [codeVersionSelected, setCodeVersionSelected] = useState<
    'python' | 'async_python' | 'typescript'
  >('async_python');

  const code =
    codeVersionSelected === 'typescript'
      ? dedent`import {
      Client
    } from "langsmith";

    const client = new Client();

    // TODO: update your search input here
    const inputs = ${searchText
      .split('\n')
      .map((line, i) => (i === 0 ? line : '    ' + line))
      .join('\n')}
    
    const examples = await client.similarExamples(
      inputs,
      '${props.datasetId}',
      3, // update this to get more examples
    );
    console.log(examples);
  `
      : getPythonSampleCode(
          searchText,
          codeVersionSelected === 'async_python' ? 'AsyncClient' : 'Client',
          codeVersionSelected === 'async_python' ? 'aclient' : 'client',
          props.datasetId,
          codeVersionSelected === 'async_python'
        );

  const selectedLightModeColor = '#f4f9f8';
  const selectedDarkModeColor = '#1c3c3c';

  return (
    <Modal open={props.open} onClose={props.onClose} sx={{ zIndex: 5 }}>
      <ModalDialog>
        <ModalClose />
        <div className="flex flex-col gap-6 px-2 pb-6">
          <div className="flex flex-col gap-0.5">
            <div className="text-lg font-semibold">
              Code Snippet for Few-Shot Search
            </div>
            <div className="text-md font-normal text-tertiary">
              {codeVersionSelected === 'typescript'
                ? 'Requires LangSmith JS SDK version 1.43 or higher'
                : 'Requires LangSmith Python SDK version 1.101 or higher'}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="plain"
              color="neutral"
              size="sm"
              onClick={() => setCodeVersionSelected('async_python')}
              sx={{
                backgroundColor:
                  codeVersionSelected === 'async_python'
                    ? isDarkMode
                      ? selectedDarkModeColor
                      : selectedLightModeColor
                    : undefined,
              }}
            >
              Async Python
            </Button>
            <Button
              variant="plain"
              color="neutral"
              size="sm"
              onClick={() => setCodeVersionSelected('python')}
              sx={{
                backgroundColor:
                  codeVersionSelected === 'python'
                    ? isDarkMode
                      ? selectedDarkModeColor
                      : selectedLightModeColor
                    : undefined,
              }}
            >
              Python
            </Button>
            <Button
              variant="plain"
              color="neutral"
              size="sm"
              onClick={() => setCodeVersionSelected('typescript')}
              sx={{
                backgroundColor:
                  codeVersionSelected === 'typescript'
                    ? isDarkMode
                      ? selectedDarkModeColor
                      : selectedLightModeColor
                    : undefined,
              }}
            >
              Typescript
            </Button>
          </div>
          <div className="flex flex-col gap-2 rounded-md bg-secondary">
            <CodeCard>
              <Code
                language={
                  new LanguageSupport(
                    codeVersionSelected === 'typescript'
                      ? typescriptLanguage
                      : pythonLanguage
                  )
                }
                value={code}
                showCopyButton
              />
            </CodeCard>
          </div>
        </div>
      </ModalDialog>
    </Modal>
  );
};

const getPythonSampleCode = (
  searchText: string,
  clientName: string,
  clientVariableName: string,
  datasetId: string,
  isAsync: boolean
) => {
  return dedent`from langsmith import ${clientName}

  ${clientVariableName} = ${clientName}()

  # TODO: update your search input here
  inputs = ${searchText
    .split('\n')
    .map((line, i) => (i === 0 ? line : '  ' + line))
    .join('\n')}

  examples = ${isAsync ? 'await ' : ''}${clientVariableName}.similar_examples(
      inputs,
      dataset_id="${datasetId}",
      limit=3, # update this to get more examples
  )
  print(examples)
  `;
};
